import type { SiteState, SiteWithId } from '~/store/site/types';
import type { UserState } from '~/store/user/types';

/** Minimal root shape for session selectors (avoids importing the configured store). */
export type SessionRootState = {
  user: UserState;
  site: SiteState;
};

export const selectUser = (state: SessionRootState): UserState => state.user;

export const selectSite = (state: SessionRootState): SiteState => state.site;

export const selectUserLoaded = (state: SessionRootState): boolean => state.user.loaded;

export const selectEffectiveRoles = (state: SessionRootState): string[] => state.user.effectiveRoles;

export const selectSelectedUserSite = (state: SessionRootState): SiteWithId | null => state.site.selectedUserSite;

export const selectSelectedSiteId = (state: SessionRootState): string | null => state.site.selectedUserSite?.id ?? null;

export const selectUserSites = (state: SessionRootState): SiteState['userSites'] => state.site.userSites;

export const selectCanImpersonate = (state: SessionRootState): boolean => state.user.canImpersonate;

export const selectCanViewCarePathways = (state: SessionRootState): boolean => state.user.canViewCarePathways;

export const selectCanViewPatientTracking = (state: SessionRootState): boolean => state.user.canViewPatientTracking;

export const selectCanAccessPatientWorkspace = (state: SessionRootState): boolean =>
  state.user.canAccessPatientWorkspace;

export const selectCanWritePatientTracking = (state: SessionRootState): boolean => state.user.canWritePatientTracking;

export const selectCanViewAllPatientTracking = (state: SessionRootState): boolean =>
  state.user.canViewAllPatientTracking;

export const selectCanWriteDocuments = (state: SessionRootState): boolean => state.user.canWriteDocuments;

export const selectCanViewInternalDocuments = (state: SessionRootState): boolean => state.user.canViewInternalDocuments;

export const selectFeatures = (state: SessionRootState): string[] => state.user.features;

export const selectFeaturesLoaded = (state: SessionRootState): boolean => state.user.featuresLoaded;

export const selectImpersonationModalOpen = (state: SessionRootState): boolean => state.site.impersonationModalOpen;

/** Combined session context for route gates and headers. */
export const selectSession = (state: SessionRootState) => ({
  user: state.user,
  selectedSite: state.site.selectedUserSite,
  selectedSiteId: state.site.selectedUserSite?.id ?? null,
  canImpersonate: state.user.canImpersonate,
  canViewCarePathways: state.user.canViewCarePathways,
  effectiveRoles: state.user.effectiveRoles,
  featuresLoaded: state.user.featuresLoaded,
});
